import React, { useEffect, useRef } from "react";
import styled from "styled-components";
import { prefersReducedMotion } from "../../styles/theme";

// Animated EEG-style traces drawn at true relative frequencies: the canvas width is a
// one-second window, so a 30 Hz trace shows 30 peaks and a 4 Hz trace shows 4. All traces
// drift left to right at the same slow speed (time is slowed, never flickered).
//
// `calm` (0–1) sets the dominant frequency on a log scale: 0 = 30 Hz (stressed),
// 0.5 ≈ 11 Hz (alpha), 1 = 4 Hz (theta, flow). Traces near that frequency are active,
// others go quiet.
// `dial`: the pointer's horizontal position sets the dominant frequency on a linear
// 40 Hz → 1 Hz scale (stress → flow → deep sleep), with a readout line, a hue that follows
// the frequency, and a tap ripple; `onFocus` reports the dominant frequency in Hz.
// `dialRange` is the [start, end] share of the width the 40 → 1 Hz scale spans.
// `interactive` / `scan` (non-dial): a calmer patch follows the pointer or drifts.

// Bands in on-screen order, highest frequency on top. Two traces per band (`hz`).
//  - amp: relative amplitude (slow waves are taller, like real EEG)
export const BANDS = [
  { key: "gamma", symbol: "γ", name: "Gamma", hz: [40, 32], amp: 0.6, light: [220, 76, 70], dark: [220, 76, 70] },
  { key: "beta", symbol: "β", name: "Beta", hz: [24, 16], amp: 0.75, light: [238, 148, 40], dark: [238, 148, 40] },
  { key: "alpha", symbol: "α", name: "Alpha", hz: [12, 9], amp: 1, light: [52, 160, 100], dark: [52, 160, 100] },
  { key: "theta", symbol: "θ", name: "Theta", hz: [6, 4], amp: 1.15, light: [22, 138, 144], dark: [120, 200, 196] },
  { key: "delta", symbol: "δ", name: "Delta", hz: [2, 1], amp: 1.3, light: [46, 90, 156], dark: [150, 180, 222] },
];

const STRESS_HZ = 30;
const FLOW_HZ = 4;
// The dial's scale runs from MAX_HZ at the left to MIN_HZ at the right, with MID_HZ at its
// centre: linear on each half, so the page centre reads a round 20 Hz.
const MAX_HZ = 40;
const MID_HZ = 20;
// Top trace position in dial mode (px), so overlaid labels can sit just above it.
export const DIAL_TOP_Y = 58;
const MIN_HZ = 1;
const SPREAD = 0.98; // 2σ² in octaves (σ ≈ 0.7 octave)

const clamp01 = (v) => Math.min(1, Math.max(0, v));
const smooth = (a, b, v) => {
  const u = clamp01((v - a) / (b - a));
  return u * u * (3 - 2 * u);
};

// Dominant frequency for a calm level (log scale, so the midpoint lands on alpha).
export const dominantHz = (calm) => STRESS_HZ * (FLOW_HZ / STRESS_HZ) ** clamp01(calm);

// Dial scale: share of the scale (0–1, left to right) ↔ frequency.
export const hzAtPosition = (pos) => {
  const p = clamp01(pos);
  return p <= 0.5 ? MAX_HZ - (MAX_HZ - MID_HZ) * (p / 0.5) : MID_HZ - (MID_HZ - MIN_HZ) * ((p - 0.5) / 0.5);
};
export const positionOfHz = (hz) => {
  const f = Math.min(MAX_HZ, Math.max(MIN_HZ, hz));
  return f >= MID_HZ ? (0.5 * (MAX_HZ - f)) / (MAX_HZ - MID_HZ) : 0.5 + (0.5 * (MID_HZ - f)) / (MID_HZ - MIN_HZ);
};

// Everything the dial drives (band engagement, flow, colour) is blended between anchor
// frequencies by *scale position*, so it changes evenly as the cursor moves.
const toPositions = (anchors) => anchors.map(([at, v]) => [positionOfHz(at), v]);
const blendAt = (pts, hz) => {
  const p = positionOfHz(hz);
  if (p <= pts[0][0]) return pts[0][1];
  for (let k = 1; k < pts.length; k += 1) {
    if (p <= pts[k][0]) {
      const u = (p - pts[k - 1][0]) / (pts[k][0] - pts[k - 1][0]);
      const e = u * u * (3 - 2 * u);
      const [from, to] = [pts[k - 1][1], pts[k][1]];
      return Array.isArray(from) ? from.map((v, c) => v + (to[c] - v) * e) : from + (to - from) * e;
    }
  }
  return pts[pts.length - 1][1];
};

// Band engagement [gamma, beta, alpha, theta, delta] at each anchor. Neighbouring bands hand
// over gradually; flow is theta-led with moderate alpha (Katahira et al., 2018); delta only
// leads in deep sleep.
const ENGAGEMENT = toPositions([
  [40, [1, 0.45, 0, 0, 0]],
  [30, [0.85, 0.8, 0.05, 0, 0]],
  [20, [0.3, 1, 0.2, 0.02, 0]],
  [13, [0.08, 0.65, 0.7, 0.12, 0]],
  [10.5, [0, 0.3, 1, 0.3, 0.02]],
  [8, [0, 0.12, 0.8, 0.6, 0.06]],
  [6, [0, 0.04, 0.55, 1, 0.12]],
  [4, [0, 0, 0.4, 0.85, 0.3]],
  [2.5, [0, 0, 0.2, 0.55, 0.7]],
  [1, [0, 0, 0.06, 0.25, 1]],
]);
// Cached at 0.05 Hz steps: traces sample this many times per frame.
const engagementCache = new Map();
const engagementAt = (hz) => {
  const key = Math.round(hz * 20);
  let v = engagementCache.get(key);
  if (!v) {
    v = blendAt(ENGAGEMENT, key / 20);
    engagementCache.set(key, v);
  }
  return v;
};

// How close a dominant frequency is to flow (0–1): full around 5–6 Hz, easing off towards
// stress on one side and sleep on the other.
const FLOW = toPositions([
  [40, 0],
  [30, 0.15],
  [20, 0.4],
  [13, 0.65],
  [8, 0.9],
  [6, 1],
  [5, 1],
  [4, 0.85],
  [2.5, 0.45],
  [1, 0.1],
]);
export const flowScore = (hz) => clamp01(blendAt(FLOW, hz));

// Colour for a frequency, blended between band colours so the hue shifts continuously.
// Each band holds its own colour across its zone and hands over near the boundary. Blending
// runs around the colour wheel (HSL), so orange → green passes through yellow, not brown.
const toHsl = ([r, g, b]) => {
  const [R, G, B] = [r / 255, g / 255, b / 255];
  const max = Math.max(R, G, B);
  const min = Math.min(R, G, B);
  const l = (max + min) / 2;
  if (max === min) return [0, 0, l];
  const d = max - min;
  const sat = l > 0.5 ? d / (2 - max - min) : d / (max + min);
  let hue;
  if (max === R) hue = (G - B) / d + (G < B ? 6 : 0);
  else if (max === G) hue = (B - R) / d + 2;
  else hue = (R - G) / d + 4;
  return [hue * 60, sat, l];
};
const toRgb = ([hue, sat, l]) => {
  const c = (1 - Math.abs(2 * l - 1)) * sat;
  const x = c * (1 - Math.abs(((hue / 60) % 2) - 1));
  const m = l - c / 2;
  const [r, g, b] = hue < 60 ? [c, x, 0] : hue < 120 ? [x, c, 0] : hue < 180 ? [0, c, x] : hue < 240 ? [0, x, c] : hue < 300 ? [x, 0, c] : [c, 0, x];
  return [r, g, b].map((v) => Math.round((v + m) * 255));
};
const HUE_HOLDS = [
  [40, 0],
  [30.8, 0],
  [29.2, 1],
  [13.5, 1],
  [12.5, 2],
  [8.3, 2],
  [7.7, 3],
  [4.15, 3],
  [3.85, 4],
  [1, 4],
];
const HUES = {
  light: toPositions(HUE_HOLDS.map(([at, bi]) => [at, toHsl(BANDS[bi].light)])),
  dark: toPositions(HUE_HOLDS.map(([at, bi]) => [at, toHsl(BANDS[bi].dark)])),
};
export const colourForHz = (hz, variant = "light") => toRgb(blendAt(HUES[variant], hz));

export const bandOfHz = (hz) => {
  if (hz >= 30) return BANDS[0];
  if (hz >= 13) return BANDS[1];
  if (hz >= 8) return BANDS[2];
  if (hz >= 4) return BANDS[3];
  return BANDS[4];
};

// How active a trace at `hz` is (0–1) when the brain's dominant frequency is `domHz`:
// its band's engagement, a little higher for the trace nearer the dominant frequency.
const traceActivity = (band, hz, domHz) => {
  const d = Math.log2(hz / domHz);
  return engagementAt(domHz)[BANDS.indexOf(band)] * (0.65 + 0.35 * Math.exp(-(d * d) / SPREAD));
};

// Band engagement (0–1) at a dominant frequency.
export const bandLevel = (band, domHz) => engagementAt(domHz)[BANDS.indexOf(band)];

const PALETTES = {
  // Quiet traces fade towards this pale neutral (a tint of their colour, never brown).
  light: { quiet: [214, 208, 200], alpha: [0.6, 1] },
  dark: { quiet: [214, 208, 200], alpha: [0.35, 0.95] },
};

const mix = (a, b, t) => a.map((v, i) => Math.round(v + (b[i] - v) * t));
const rgba = (c, a) => `rgba(${c[0]},${c[1]},${c[2]},${a})`;
const TAU = Math.PI * 2;

// Absolutely positioned so the canvas's pixel size never feeds back into layout.
const Canvas = styled.canvas`
  position: absolute;
  inset: 0;
  display: block;
  width: 100%;
  height: 100%;
  touch-action: pan-y;
`;

const NeuralField = ({ calm = 0, interactive = false, scan = false, dial = false, dialRange = [0, 1], onFocus, variant = "light", lines = 9, className }) => {
  const canvasRef = useRef(null);
  const calmRef = useRef(calm);
  const scanRef = useRef(scan);
  const onFocusRef = useRef(onFocus);

  useEffect(() => {
    calmRef.current = calm;
  }, [calm]);
  useEffect(() => {
    scanRef.current = scan;
  }, [scan]);
  useEffect(() => {
    onFocusRef.current = onFocus;
  }, [onFocus]);

  useEffect(() => {
    const canvas = canvasRef.current;
    const ctx = canvas.getContext("2d");
    const palette = PALETTES[variant];
    const reduced = prefersReducedMotion();
    const listen = interactive || dial;
    let w = 0;
    let h = 0;
    let raf = 0;
    let visible = true;
    let lastReport = 0;

    const resize = () => {
      const dpr = Math.min(window.devicePixelRatio || 1, 2);
      const rect = canvas.getBoundingClientRect();
      w = rect.width;
      h = rect.height;
      canvas.width = Math.round(w * dpr);
      canvas.height = Math.round(h * dpr);
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
    };
    resize();
    const ro = new ResizeObserver(resize);
    ro.observe(canvas);

    const io = new IntersectionObserver(([entry]) => {
      visible = entry.isIntersecting;
    });
    io.observe(canvas);

    const pointer = { x: w / 2, y: h / 2, active: 0, target: 0, last: 0 };
    let bandX = w / 2;
    let dialX = w / 2;
    let smoothCalm = calmRef.current;
    let bandStrength = 0;
    const ripples = [];

    const locate = (e) => {
      const point = e.touches ? e.touches[0] : e;
      if (!point) return null;
      const rect = canvas.getBoundingClientRect();
      const x = point.clientX - rect.left;
      const y = point.clientY - rect.top;
      if (y < -40 || y > rect.height + 40 || x < 0 || x > rect.width) return null;
      return { x, y };
    };
    const onMove = (e) => {
      const p = locate(e);
      if (!p) return;
      pointer.x = p.x;
      pointer.y = p.y;
      pointer.target = 1;
      pointer.last = performance.now();
    };
    const onDown = (e) => {
      const p = locate(e);
      if (!p || p.y < 0 || p.y > h) return;
      onMove(e);
      ripples.push({ x: p.x, y: p.y, start: performance.now() });
      if (ripples.length > 4) ripples.shift();
    };
    if (listen) {
      window.addEventListener("pointermove", onMove, { passive: true });
      window.addEventListener("touchmove", onMove, { passive: true });
    }
    if (dial) window.addEventListener("pointerdown", onDown, { passive: true });

    const [dialLo, dialHi] = dialRange;

    const draw = (now) => {
      raf = requestAnimationFrame(draw);
      if (!visible || document.hidden || !w) return;
      // Seconds of signal travelled; ~40 px/s on screen regardless of width.
      const shift = reduced ? 0 : (now / 1000) * (40 / w);

      if (now - pointer.last > 2200) pointer.target = 0;
      pointer.active += (pointer.target - pointer.active) * 0.05;

      let level;
      if (dial) {
        // The pointer is the dial; when idle it sweeps the range on its own.
        const lo = w * dialLo;
        const hi = w * dialHi;
        const idle = lo + (hi - lo) * clamp01(0.5 + 0.56 * Math.sin(now * 0.00022));
        const target = Math.min(hi, Math.max(lo, idle + (pointer.x - idle) * pointer.active));
        dialX += (target - dialX) * 0.1;
        level = clamp01((dialX - lo) / Math.max(1, hi - lo));
      } else {
        const driftX = w * (0.5 + 0.32 * Math.sin(now * 0.00022));
        const targetX = driftX + (pointer.x - driftX) * pointer.active;
        bandX += (targetX - bandX) * 0.08;
        smoothCalm += (calmRef.current - smoothCalm) * 0.06;
        const bandOn = interactive || scanRef.current;
        bandStrength += ((bandOn ? 1 : 0) - bandStrength) * 0.06;
        level = smoothCalm;
      }

      const radius = Math.max(120, w * 0.15);
      const bandFactor = (0.6 + 0.4 * pointer.active) * bandStrength;
      const localCalm = (x) => Math.min(1, level + Math.exp(-((x - bandX) ** 2) / (2 * radius * radius)) * bandFactor);
      const domHz = dial ? hzAtPosition(level) : dominantHz(level);
      // Jagged edges belong to the fast, stressed end of the scale.
      const dialStress = smooth(8, 30, domHz);

      const amp = Math.min(h * 0.045, 26);
      // Dial mode leaves a fixed band for the state words above (DIAL_TOP_Y) and runs into the
      // ruler below.
      const topY = dial ? DIAL_TOP_Y : h * 0.12;
      const bottomY = dial ? h - 14 : h * 0.88;
      const step = w < 700 ? 1 : 2;
      const dots = [];

      const width = w; // frame-local copy for the per-trace closures
      ctx.clearRect(0, 0, w, h);

      if (dial) {
        // Soft hue around the cursor, coloured by the frequency, beneath the traces.
        const midY = (topY + bottomY) / 2;
        const hueY = midY + (Math.min(h, Math.max(0, pointer.y)) - midY) * pointer.active;
        const hueR = 64;
        const hueTone = colourForHz(domHz, variant);
        const hue = ctx.createRadialGradient(dialX, hueY, 0, dialX, hueY, hueR);
        hue.addColorStop(0, rgba(hueTone, 0.34));
        hue.addColorStop(0.4, rgba(hueTone, 0.14));
        hue.addColorStop(1, rgba(hueTone, 0));
        ctx.fillStyle = hue;
        ctx.fillRect(dialX - hueR, hueY - hueR, hueR * 2, hueR * 2);
      }

      for (let i = 0; i < lines; i += 1) {
        const bi = Math.min(BANDS.length - 1, Math.floor((i * BANDS.length) / lines));
        const band = BANDS[bi];
        // Within a band, the first trace takes the higher frequency.
        const hz = band.hz[Math.min(1, i - Math.ceil((bi * lines) / BANDS.length))];
        const base = topY + ((bottomY - topY) * i) / Math.max(1, lines - 1);
        const phase = i * 1.7;
        const colour = band[variant];
        const centreWeight = 1 - Math.abs(i - (lines - 1) / 2) / Math.max(1, (lines - 1) / 2);
        const baseAlpha = palette.alpha[0] + (palette.alpha[1] - palette.alpha[0]) * centreWeight;
        const act = traceActivity(band, hz, domHz);

        // Colour follows activity: active traces in band colour, quiet ones muted.
        const lineColour = mix(palette.quiet, colour, 0.3 + 0.7 * act);
        const lineAlpha = baseAlpha * (0.4 + 0.6 * act);

        const grad = ctx.createLinearGradient(0, 0, w, 0);
        if (dial) {
          grad.addColorStop(0, rgba(lineColour, lineAlpha));
          grad.addColorStop(1, rgba(lineColour, lineAlpha));
        } else {
          const lo = clamp01((bandX - radius * 1.8) / w);
          const mid = clamp01(bandX / w);
          const hi = clamp01((bandX + radius * 1.8) / w);
          const midAct = traceActivity(band, hz, dominantHz(localCalm(bandX)));
          const midColour = mix(palette.quiet, colour, 0.3 + 0.7 * midAct);
          const midAlpha = baseAlpha * (0.4 + 0.6 * midAct);
          grad.addColorStop(0, rgba(lineColour, lineAlpha));
          grad.addColorStop(lo, rgba(lineColour, lineAlpha));
          grad.addColorStop(mid, rgba(midColour, lineAlpha + (midAlpha - lineAlpha) * bandStrength));
          grad.addColorStop(hi, rgba(lineColour, lineAlpha));
          grad.addColorStop(1, rgba(lineColour, lineAlpha));
        }

        const yAt = (x) => {
          const a = dial ? act : traceActivity(band, hz, dominantHz(localCalm(x)));
          const stress = dial ? dialStress : 1 - localCalm(x);
          // Signal time at this x (one-second window), moving left to right.
          const s = x / width - shift;
          const env = 0.78 + 0.22 * Math.sin(TAU * 0.9 * s + phase);
          // Under stress the wave carries a harmonic and sharp peaks, at the same frequency.
          const core = Math.sin(TAU * hz * s + phase);
          const edge = Math.sin(TAU * hz * 2 * s + phase * 2) * 0.3 + Math.pow(Math.max(0, core), 9) * 0.9;
          return base + amp * band.amp * a * env * (core + stress * edge);
        };

        ctx.beginPath();
        for (let x = 0; x <= w + step; x += step) {
          const y = yAt(x);
          if (x === 0) ctx.moveTo(x, y);
          else ctx.lineTo(x, y);
        }
        ctx.strokeStyle = grad;
        ctx.lineWidth = 1.1 + centreWeight * 0.6 + act * 0.9;
        ctx.stroke();

        if (dial) dots.push({ y: yAt(dialX), colour, act });
      }

      if (dial) {
        const tone = colourForHz(domHz, variant);

        const lineTop = topY - 8;
        const lineBottom = h;

        // Readout line.
        const needle = ctx.createLinearGradient(0, lineTop, 0, lineBottom);
        needle.addColorStop(0, rgba(tone, 0));
        needle.addColorStop(0.15, rgba(tone, 0.45));
        needle.addColorStop(1, rgba(tone, 0.3));
        ctx.strokeStyle = needle;
        ctx.lineWidth = 1;
        ctx.beginPath();
        ctx.moveTo(dialX, lineTop);
        ctx.lineTo(dialX, lineBottom);
        ctx.stroke();

        // Crossing dots ride each trace; bright on active bands, faint on quiet ones.
        dots.forEach((d) => {
          ctx.fillStyle = rgba(mix(palette.quiet, d.colour, 0.3 + 0.7 * d.act), 0.35 + 0.65 * d.act);
          ctx.beginPath();
          ctx.arc(dialX, d.y, 1.5 + 1.5 * d.act, 0, TAU);
          ctx.fill();
        });

        // Tap ripples.
        for (let r = ripples.length - 1; r >= 0; r -= 1) {
          const k = (now - ripples[r].start) / 900;
          if (k >= 1) {
            ripples.splice(r, 1);
          } else {
            ctx.strokeStyle = rgba(tone, 0.5 * (1 - k));
            ctx.lineWidth = 1.5;
            ctx.beginPath();
            ctx.arc(ripples[r].x, ripples[r].y, 8 + 56 * (1 - (1 - k) ** 3), 0, TAU);
            ctx.stroke();
          }
        }
      }

      if (onFocusRef.current && now - lastReport > 120) {
        lastReport = now;
        if (dial) {
          onFocusRef.current(domHz);
        } else {
          const engaged = interactive ? pointer.active : 0;
          onFocusRef.current(clamp01(smoothCalm + (1 - smoothCalm) * engaged * 0.92));
        }
      }
    };
    raf = requestAnimationFrame(draw);

    return () => {
      cancelAnimationFrame(raf);
      ro.disconnect();
      io.disconnect();
      window.removeEventListener("pointermove", onMove);
      window.removeEventListener("touchmove", onMove);
      window.removeEventListener("pointerdown", onDown);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [interactive, dial, dialRange[0], dialRange[1], variant, lines]);

  return <Canvas ref={canvasRef} className={className} aria-hidden="true" />;
};

export default NeuralField;
